import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, Unlock } from "lucide-react";
import { finApi, finErr, money } from "@/lib/financeApi";
import { day } from "@/components/nds/windows/finance/flow/TxnCard";

const field = "bg-black/40 border border-white/10 rounded-sm px-2 py-1.5 text-xs text-nex-silver focus:outline-none focus:border-nex-gold/60 [color-scheme:dark]";
const letters = (s) => (s.match(/[A-Za-z]/g) || []).length;
export const LOCK_FIELDS = [["rent_paid", "Rent settled"], ["cm_paid", "Case mgmt settled"], ["rent_due", "Rent owed"], ["cm_due", "PACT Fee owed"],
  ["paycheck_logged", "Paycheck logged"], ["cash_in_hand", "Cash in Hand"], ["savings_week", "Savings this week"], ["loc_owed", "LOC owed"], ["loc_paid", "LOC paid"]];

/** Super admin types the week's numbers; they're saved exactly as typed and the week is locked. */
export function LockWeek({ rid, x, onDone, onCancel }) {
  const [vals, setVals] = useState(() => Object.fromEntries(LOCK_FIELDS.map(([k]) => [k, String(x?.[k] ?? 0)])));
  const [note, setNote] = useState("");
  const [inv, setInv] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [cashTyped, setCashTyped] = useState(false);
  useEffect(() => { finApi.get(`/flow/dues/${rid}/lock/${x.week_ending}/check`).then((d) => setInv(d.invoice || null)).catch(() => setInv(null)); }, [rid, x.week_ending]);
  if (inv === undefined) return <Loader2 className="h-4 w-4 animate-spin text-nex-muted" data-testid="lockw-loading" />;
  if (inv) return <p className="text-xs text-amber-200" data-testid="lockw-invoice-block">This week has invoiced paycheck #{inv} tied to it. Void invoice #{inv} first, then you can correct this week.</p>;
  const change = (k, v) => {
    const next = { ...vals, [k]: v };
    if (k === "cash_in_hand") setCashTyped(true);
    if (k === "savings_week" && !cashTyped) next.cash_in_hand = String(Math.round(((x?.cash_in_hand || 0) - ((Number(v) || 0) - (x?.savings_week || 0))) * 100) / 100);  // assigned from Cash in Hand
    setVals(next);
  };
  const save = async () => {
    // LOC follows the Rent / PACT Fee figures, so it's only locked when typed differently
    const values = Object.fromEntries(LOCK_FIELDS.map(([k]) => [k, Number(vals[k]) || 0]).filter(([k, v]) => !k.startsWith("loc_") || Math.abs(v - (x?.[k] || 0)) >= 0.005));
    setBusy(true);
    try {
      await finApi.post(`/flow/dues/${rid}/lock`, { week: x.week_ending, values, note });
      toast.success(`Week ending ${day(x.week_ending)} saved and locked`);
      onDone?.();
    } catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="lockw-form">
      <p className="text-[11px] text-nex-muted">Whatever you type is saved exactly as entered and locked. Invoices, paychecks and statements won't change it. Balances get a correction for the difference. Bank and petty cash don't move.</p>
      <div className="grid grid-cols-2 gap-2">
        {LOCK_FIELDS.map(([k, label]) => (
          <label key={k} className="text-[11px] text-nex-muted space-y-1">
            <span className="block">{label} <span className="font-mono text-nex-muted/70">now {money(x?.[k] || 0)}</span></span>
            <input type="number" step="0.01" value={vals[k]} onChange={(e) => change(k, e.target.value)} className={`${field} w-full font-mono`} data-testid={`lockw-${k}`} />
          </label>))}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this correction (at least 5 letters)" rows={2} className={`${field} w-full`} data-testid="lockw-note" />
      <div className="flex items-center gap-3">
        <button type="button" disabled={busy || letters(note) < 5} onClick={save} data-testid="lockw-save" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-nex-gold text-black rounded-sm text-[11px] font-mono uppercase tracking-[0.12em] disabled:opacity-40">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Lock className="h-3 w-3" />}Save & lock week</button>
        <button type="button" onClick={onCancel} data-testid="lockw-cancel" className="text-[11px] text-nex-muted hover:text-nex-gold">Cancel</button>
      </div>
    </div>
  );
}

/** The week card's savings slot: what went to savings this week, and (super admin) assign more from Cash in Hand. */
export function SavingsSlot({ rid, w, sa, onDone }) {
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await finApi.post(`/flow/dues/${rid}/fix`, { action: "bucket", week: w.week_ending, from_bucket: "checking", to_bucket: "savings", amount: Number(amt), note });
      toast.success(`${money(Number(amt))} assigned to savings`);
      setOpen(false); setAmt(""); setNote(""); onDone?.(); window.dispatchEvent(new CustomEvent("fin:money-changed"));
    } catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-nex-muted" data-testid={`flow-duesr-savings-slot-${w.week_ending}`}>
      <span>{(w.savings_week || 0) < 0 ? "Taken from savings this week" : "Saved this week"} <span className="font-mono text-sky-200" data-testid={`flow-duesr-savings-week-${w.week_ending}`}>{money(Math.abs(w.savings_week || 0))}</span></span>
      {sa && !w.locked && !open && <button type="button" onClick={() => setOpen(true)} data-testid={`flow-duesr-savings-assign-${w.week_ending}`} className="text-nex-gold hover:underline">+ Assign to savings</button>}
      {sa && w.locked && <span className="text-nex-muted/70">locked · change it in Correct this week</span>}
      {open && <>
        <input type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Amount from Cash in Hand" className={`${field} w-36 font-mono`} data-testid={`flow-duesr-savings-amount-${w.week_ending}`} />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note" className={`${field} w-40`} data-testid={`flow-duesr-savings-note-${w.week_ending}`} />
        <button type="button" disabled={busy || !(Number(amt) > 0) || letters(note) < 5} onClick={go} data-testid={`flow-duesr-savings-save-${w.week_ending}`} className="text-nex-gold disabled:opacity-40">{busy ? "…" : "Save"}</button>
        <button type="button" onClick={() => setOpen(false)} data-testid={`flow-duesr-savings-cancel-${w.week_ending}`}>Cancel</button></>}
    </div>
  );
}

/** Remove a week's lock: its corrections are reversed and the week goes back to the automatic numbers. */
export function UnlockWeek({ rid, w, onDone }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!w.locked) return null;
  const go = async () => {
    setBusy(true);
    try {
      await finApi.post(`/flow/dues/${rid}/lock/${w.week_ending}/unlock`, { note });
      toast.success("Week unlocked — back to the automatic numbers");
      setOpen(false); onDone?.(); window.dispatchEvent(new CustomEvent("fin:money-changed"));
    } catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} data-testid={`flow-duesr-unlock-${w.week_ending}`} className="inline-flex items-center gap-1 text-[11px] text-nex-muted hover:text-nex-gold"><Unlock className="h-3 w-3" />Unlock</button>;
  return (
    <span className="flex items-center gap-2" data-testid={`flow-duesr-unlock-form-${w.week_ending}`}>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why unlock?" className={`${field} w-40`} data-testid={`flow-duesr-unlock-note-${w.week_ending}`} />
      <button type="button" disabled={busy || letters(note) < 5} onClick={go} data-testid={`flow-duesr-unlock-confirm-${w.week_ending}`} className="text-[11px] text-nex-gold disabled:opacity-40">{busy ? "…" : "Confirm"}</button>
      <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-nex-muted" data-testid={`flow-duesr-unlock-cancel-${w.week_ending}`}>Cancel</button>
    </span>
  );
}
